import { useCallback } from "react";
import { useApi } from "..";
import { MedicalRecordsEntityResponse } from "./interfaces/medicalRecords.response";
import { MedicalRecordsCreateRequest } from "./interfaces/medicalRecordsCreate.request";
import { OptionsResponse } from "./interfaces/options.response";

export interface GeneratedDocument {
  type: string;
  filename: string;
  url: string;
}

export interface MedicinePaginatedResponse {
  data: OptionsResponse[];
  total: number;
  page: number;
  limit: number;
}

export interface AutocompleteRequest {
  text: string;
  field: string;
}

export interface AutocompleteResponse {
  suggestion: string;
}

export const useMedicalRecordService = () => {
  const { post, get } = useApi();

  const postCreateMedicalRecord = useCallback(
    ({ body }: MedicalRecordsCreateRequest) => {
      return post<MedicalRecordsEntityResponse>("/medical-record/create", body);
    },
    [post]
  );

  const getRecords = useCallback(
    async (appointmentId: number) => {
      const { data } = await get<MedicalRecordsEntityResponse[]>(
        "/medical-record/find/" + appointmentId
      );
      return data;
    },
    [get]
  );

  const getRecordByAppointment = useCallback(
    async (id: number) => {
      return get<MedicalRecordsEntityResponse>(
        "/medical-record/find-one-appointment/" + id
      ).then((response) => {
        return response.data;
      });
    },
    [get]
  );

  const getMedicine = useCallback(
    async (term: string) => {
      const { data } = await get<OptionsResponse[]>(
        "/medicine/search?term=" + term
      );
      return data;
    },
    [get]
  );

  const getMedicinePaginated = useCallback(
    async (page: number = 1, limit: number = 20, term?: string) => {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (term) params.append("term", term);

      const { data } = await get<MedicinePaginatedResponse>(
        `/medicine/paginated?${params.toString()}`
      );
      return data;
    },
    [get]
  );

  const getMedicineByNames = useCallback(
    async (names: string[]) => {
      const { data } = await get<OptionsResponse[]>(
        `/medicine/name?names=${names.join(",")}`
      );
      return data;
    },
    [get]
  );

  const getComorbidities = useCallback(async () => {
    const { data } = await get<OptionsResponse[]>("/comorbidity/find-all");
    return data;
  }, [get]);

  const searchComorbidities = useCallback(
    async (term: string) => {
      const { data } = await get<OptionsResponse[]>(
        `/comorbidity/search?term=${term}`
      );
      return data;
    },
    [get]
  );

  const getAutocomplete = useCallback(
    async (request: AutocompleteRequest): Promise<AutocompleteResponse> => {
      const { data } = await post<AutocompleteResponse>(
        "/openrouter/autocomplete",
        request
      );
      return data;
    },
    [post]
  );

  const generatePdfs = useCallback(
    async (
      appointmentId: number,
      appointmentStartTime: string
    ): Promise<GeneratedDocument[]> => {
      const { data } = await post<GeneratedDocument[]>(
        `/medical-record/generate-pdfs/${appointmentId}`,
        { appointmentStartTime }
      );
      return data;
    },
    [post]
  );

  const startSignature = useCallback(
    (appointmentId: number): string => {
      return `${process.env.REACT_APP_API_URL}/appointment/${appointmentId}/start-signature?origin=web`;
    },
    []
  );

  const createMedicine = useCallback(
    async (name: string): Promise<OptionsResponse> => {
      const { data } = await post<OptionsResponse>("/medicine/create", { name });
      return data;
    },
    [post]
  );

  const createComorbidity = useCallback(
    async (name: string): Promise<OptionsResponse> => {
      const { data } = await post<OptionsResponse>("/comorbidity/create", { name });
      return data;
    },
    [post]
  );

  return {
    postCreateMedicalRecord,
    getRecords,
    getMedicine,
    getMedicinePaginated,
    getComorbidities,
    searchComorbidities,
    getMedicineByNames,
    getRecordByAppointment,
    getAutocomplete,
    generatePdfs,
    startSignature,
    createMedicine,
    createComorbidity,
  };
};
